import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CheckCircle } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { Reveal, RiseHeading } from "@/components/motion";
import { IMG, BRAND } from "@/lib/data";

const APPROACH = [
  { n: "01", title: "Precision first", desc: "Every measurement, cut and fit is treated as non-negotiable. Glass and aluminium leave no room for approximation — and neither do we." },
  { n: "02", title: "Custom by default", desc: "We don't sell off-the-shelf. Each solution is designed around your space, your dimensions and your design intent." },
  { n: "03", title: "Clean installation", desc: "Our on-site work respects your home or business — careful handling, tidy execution and a flawless final finish." },
];

const WORK_WITH = ["Homeowners", "Architects", "Interior Designers", "Builders & Developers", "Offices", "Retail Stores & Showrooms"];

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-6%", "6%"]);

  return (
    <>
      <PageHeader
        overline="About · Ahmedabad"
        title={
          <>
            Fifteen years of <br />
            <span className="italic">clear thinking.</span>
          </>
        }
        description="Nagar Glass is a glass and aluminium studio serving Ahmedabad and nearby areas. We turn raw glass and aluminium into windows, doors, enclosures, partitions, railings, mirrors and storefronts — built to last."
        testid="about-header"
        image={IMG.aluminium}
      />

      {/* Founder story */}
      <section ref={ref} data-testid="about-founder" className="bg-white py-24 md:py-32">
        <div className="mx-auto grid max-w-[1400px] gap-14 px-5 md:grid-cols-12 md:gap-16 md:px-8">
          <div className="md:col-span-5">
            <Reveal>
              <div className="relative">
                <div className="absolute -left-4 -top-4 hidden h-full w-full rounded-sm border border-gold md:block" />
                <div className="relative overflow-hidden rounded-sm border border-black/10">
                  <motion.img style={{ y }} src={BRAND.ownerPhoto} alt={BRAND.ownerName} className="aspect-[4/5] h-full w-full object-cover" />
                </div>
              </div>
            </Reveal>
          </div>
          <div className="flex flex-col justify-center md:col-span-7">
            <span className="eyebrow rule">Meet the Founder</span>
            <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.1] tracking-tight text-ink md:text-5xl">
              A studio built on <span className="italic">craftsmanship and trust.</span>
            </RiseHeading>
            <Reveal delay={0.15}>
              <p className="mt-8 text-base leading-relaxed text-muted-foreground md:text-lg">
                For over 15 years, Nagar Glass has delivered custom glass and aluminium solutions
                for residential and commercial projects across Ahmedabad. We work equally fluently
                on a single home bathroom enclosure and a full commercial storefront — with the same
                care for detail and finish.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="mt-4 text-base leading-relaxed text-muted-foreground md:text-lg">
                Our strength is simple: we listen, we measure precisely, we fabricate to spec, and
                we install cleanly. No shortcuts, no compromises on quality.
              </p>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-8 border-l-2 border-gold pl-5">
                <div className="font-display text-xl font-semibold text-ink">{BRAND.ownerName}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{BRAND.ownerTitle}</div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Approach */}
      <section className="bg-cream/40 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <span className="eyebrow rule">Our Approach</span>
          <RiseHeading className="mt-5 max-w-3xl font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
            Three principles <span className="italic">we don't bend.</span>
          </RiseHeading>
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-3">
            {APPROACH.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.1}>
                <div className="h-full border-t-2 border-ink bg-white p-7 md:p-8">
                  <span className="font-display text-3xl italic text-gold">{a.n}</span>
                  <h3 className="mt-5 font-display text-xl font-medium tracking-tight text-ink md:text-2xl">{a.title}</h3>
                  <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-base">{a.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Experience strip */}
      <section className="border-y border-black/10 bg-white py-16 md:py-20">
        <div className="mx-auto grid max-w-[1400px] grid-cols-1 gap-10 px-5 md:grid-cols-3 md:px-8">
          {[
            { k: "15+", v: "Years of hands-on experience" },
            { k: "2", v: "Showrooms across Ahmedabad" },
            { k: "R + C", v: "Residential & commercial expertise" },
          ].map((s, i) => (
            <Reveal key={s.k} delay={i * 0.1}>
              <div className="border-t-2 border-ink pt-6">
                <div className="font-display text-5xl font-medium tracking-tight text-ink md:text-6xl">{s.k}</div>
                <p className="mt-4 text-sm text-muted-foreground">{s.v}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Who we work with */}
      <section className="bg-offwhite py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-5 md:px-8">
          <div className="grid grid-cols-1 gap-14 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="eyebrow rule">Who We Work With</span>
              <RiseHeading className="mt-5 font-display text-3xl font-medium leading-[1.05] tracking-tight text-ink md:text-5xl">
                From homeowners <span className="italic">to developers.</span>
              </RiseHeading>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-1 sm:grid-cols-2">
                {WORK_WITH.map((w, i) => (
                  <Reveal key={w} delay={(i % 2) * 0.08}>
                    <div className="flex items-center gap-4 border-b border-black/10 py-5">
                      <CheckCircle weight="fill" size={22} className="shrink-0 text-gold" />
                      <span className="font-display text-lg font-medium tracking-tight text-ink">{w}</span>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
};

export default About;
